import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, thumbUrl } from "@/lib/api";

function NumberSearch({ eventId }) {
  const [num, setNum] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!num) return;
    setBusy(true);
    try {
      const r = await api.get("/images", { params: { event_id: eventId, number: num } });
      setResults(Array.isArray(r.data) ? r.data : []);
    } catch {
      setResults([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-10 border border-white/10 bg-[#0a0a0a] p-6" data-testid="event-number-search">
      <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Find your number</p>
      <p className="text-sm text-white/50 mt-1">Type your race number to jump straight to your photos from this event.</p>
      <form onSubmit={search} className="mt-4 flex max-w-md">
        <div className="relative flex-1">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 pm-orange font-display text-lg pointer-events-none">#</span>
          <input
            type="text"
            inputMode="numeric"
            value={num}
            onChange={(e) => { setNum(e.target.value.replace(/\D/g, "").slice(0, 4)); setResults(null); }}
            placeholder="e.g. 27"
            className="w-full bg-black/60 border border-white/25 pl-9 pr-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[var(--pm-orange)] transition-colors"
            data-testid="event-number-input"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !num}
          className="bg-[var(--pm-orange)] text-black px-6 text-xs uppercase tracking-[0.25em] font-bold hover:opacity-90 transition disabled:opacity-50"
          data-testid="event-number-submit"
        >
          {busy ? "…" : "Find"}
        </button>
      </form>
      {results !== null && (
        <div className="mt-6" data-testid="event-number-results">
          {results.length === 0 ? (
            <p className="text-white/50 text-sm">
              No photos tagged #{num} at this event yet — some numbers are hard to read automatically, so it's worth browsing the class galleries below too.
            </p>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-4">
                <span className="pm-orange font-semibold">{results.length}</span> photo{results.length !== 1 ? "s" : ""} of #{num}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {results.map((img) => (
                  <Link key={img.id} to={`/image/${img.id}`} className="gallery-card block aspect-[4/5]" data-testid={`event-number-result-${img.id}`}>
                    <img src={thumbUrl(img.id)} alt={img.title} loading="lazy" className="w-full h-full object-cover" />
                    <div className="reveal">
                      <div>
                        <h4 className="font-serif text-lg text-white leading-tight">{img.title}</h4>
                      </div>
                      <span className="text-xs uppercase tracking-[0.2em] text-white border border-white/40 px-2 py-1.5">View</span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function EventPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/events/${id}`)
      .then((r) => { setEvent(r.data.event); setGroups(r.data.groups); })
      .catch(() => { setEvent(null); })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-32 text-center text-white/50 text-sm uppercase tracking-[0.3em]">Loading…</div>;
  if (!event) return (
    <div className="pt-32 text-center">
      <p className="text-white/50 text-sm uppercase tracking-[0.3em]">Event not found.</p>
      <button onClick={() => navigate("/")} className="mt-6 text-white underline-offset-4 hover:underline text-sm">Back to events</button>
    </div>
  );

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-8 pb-24" data-testid="event-page">
      <Link to="/" className="text-xs uppercase tracking-[0.3em] text-white/50 hover:text-white" data-testid="back-to-events">
        ← All events
      </Link>

      <div className="mt-8 border-b border-white/10 pb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-white/40">{fmtDate(event.date)}</p>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-none mt-3" data-testid="event-track">{event.track}</h1>
        {event.name && <p className="font-serif text-2xl text-white/70 mt-4 italic">{event.name}</p>}
        {event.description && <p className="text-white/65 mt-6 max-w-2xl leading-relaxed">{event.description}</p>}
        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-6">
          {event.group_count} {event.group_count === 1 ? "class" : "classes"} · {event.photo_count} photos
        </p>
      </div>

      <NumberSearch eventId={event.id} />

      <div className="mt-10">
        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-6">Select your class</p>
        {groups.length === 0 ? (
          <p className="text-white/50 text-sm py-12">No classes listed for this event yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((g, idx) => (
              <Link
                key={g.id}
                to={`/group/${g.id}`}
                className="gallery-card block aspect-[4/3] fade-up"
                style={{ animationDelay: `${idx * 50}ms` }}
                data-testid={`group-card-${g.id}`}
              >
                {g.cover_image_id ? (
                  <img src={thumbUrl(g.cover_image_id)} alt={g.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#101010] grid place-items-center">
                    <span className="text-white/30 text-xs uppercase tracking-[0.3em]">No cover yet</span>
                  </div>
                )}
                <div className="reveal">
                  <div>
                    <h3 className="font-serif text-3xl text-white leading-tight">{g.name}</h3>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60 mt-3">
                      {g.photo_count} {g.photo_count === 1 ? "photo" : "photos"}
                    </p>
                  </div>
                  <span className="text-xs uppercase tracking-[0.2em] text-white border border-white/40 px-3 py-2">View</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
